/** Canonical public label: "September, 2027" */
export const FLEXIBLE_EVENT_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTH_LOOKUP = new Map<string, number>(
  FLEXIBLE_EVENT_MONTHS.flatMap((name, index) => [
    [name.toLowerCase(), index + 1],
    [name.slice(0, 3).toLowerCase(), index + 1],
  ]),
);

export type FlexibleEventMonthYear = {
  month: number;
  year: number;
};

export type EventDateDisplayMode = "scheduled" | "comingSoon" | "monthYear";

export function getEventDateDisplayMode(
  event: {
    comingSoonDate?: boolean;
    comingSoonDateMessage?: string;
  } | null | undefined,
): EventDateDisplayMode {
  if (!event?.comingSoonDate) {
    return "scheduled";
  }
  return parseFlexibleEventMonthYear(event.comingSoonDateMessage)
    ? "monthYear"
    : "comingSoon";
}

export function formatFlexibleEventMonthYear(
  month: number,
  year: number,
): string {
  const name = FLEXIBLE_EVENT_MONTHS[month - 1];
  if (!name || !Number.isFinite(year)) {
    return "";
  }
  return `${name}, ${year}`;
}

export function parseFlexibleEventMonthYear(
  raw: string | null | undefined,
): FlexibleEventMonthYear | null {
  const text = String(raw ?? "").trim();
  if (!text) {
    return null;
  }

  const monthYear = /^([A-Za-z]+)\s*,?\s*(\d{4})$/.exec(text);
  if (monthYear) {
    const month = MONTH_LOOKUP.get(monthYear[1].toLowerCase());
    const year = Number(monthYear[2]);
    if (month && year >= 1900 && year <= 3000) {
      return { month, year };
    }
  }

  const numeric = /^(\d{1,2})[/-](\d{4})$/.exec(text);
  if (numeric) {
    const month = Number(numeric[1]);
    const year = Number(numeric[2]);
    if (month >= 1 && month <= 12 && year >= 1900 && year <= 3000) {
      return { month, year };
    }
  }

  const iso = /^(\d{4})-(\d{1,2})$/.exec(text);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    if (month >= 1 && month <= 12 && year >= 1900 && year <= 3000) {
      return { month, year };
    }
  }

  return null;
}

export function formatFlexibleEventDateLabel(
  message: string | null | undefined,
  fallback = "",
): string {
  const parsed = parseFlexibleEventMonthYear(message);
  if (parsed) {
    return formatFlexibleEventMonthYear(parsed.month, parsed.year);
  }
  const trimmed = String(message ?? "").trim();
  return trimmed || fallback;
}

export function buildFlexibleEventYearOptions(
  now = new Date(),
): { id: string; name: string }[] {
  const currentYear = now.getUTCFullYear();
  const years: { id: string; name: string }[] = [];
  for (let year = currentYear - 1; year <= currentYear + 15; year += 1) {
    years.push({ id: String(year), name: String(year) });
  }
  return years;
}

export function buildFlexibleEventMonthOptions(): { id: string; name: string }[] {
  return FLEXIBLE_EVENT_MONTHS.map((name, index) => ({
    id: String(index + 1),
    name,
  }));
}
